/**
 * Client-side DRM service for license management and device fingerprinting
 */

export interface DRMCapabilities {
  widevine: boolean
  fairplay: boolean
  playready: boolean
  userAgent: string
  platform: string
}

export interface DeviceFingerprint {
  deviceId: string
  userAgent: string
  platform: string
  screenResolution: string
  timezone: string
  language: string
  hardwareConcurrency: number
  maxTouchPoints: number
  colorDepth: number
  pixelRatio: number
}

export interface LicenseRequest {
  cosmkId: string
  drmSystem: string
  deviceInfo: DeviceFingerprint
  userInfo: {
    userId: string
    sessionId: string
  }
  message?: ArrayBuffer
}

export interface LicenseResponse {
  success: boolean
  license?: ArrayBuffer
  licenseInfo?: {
    licenseId: string
    drmSystem: string
    issuedAt: string
    expiresAt: string
    permissions: any
    forensicId: string
  }
  error?: string
}

class DRMClientService {
  private deviceFingerprint: DeviceFingerprint | null = null
  private supportedDRM: string[] = []
  private activeLicenses: Map<string, any> = new Map()

  constructor() {
    this.initialize()
  }

  private async initialize() {
    this.deviceFingerprint = await this.generateDeviceFingerprint()
    this.supportedDRM = await this.detectSupportedDRM()
    console.log('DRM Client Service initialized', {
      supportedDRM: this.supportedDRM,
      deviceId: this.deviceFingerprint.deviceId
    })
  }

  /**
   * Validate COSMKid format
   */
  validateCOSMKid(cosmkId: string): boolean {
    if (!cosmkId || typeof cosmkId !== 'string') {
      return false
    }
    
    // COSMKids are 64-character hex strings
    const hexPattern = /^[a-f0-9]{64}$/
    return hexPattern.test(cosmkId)
  }

  /**
   * Generate unique device fingerprint
   */
  private async generateDeviceFingerprint(): Promise<DeviceFingerprint> {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    
    // Generate canvas fingerprint
    if (ctx) {
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('CosmicVault DRM Fingerprint', 2, 2)
    }
    const canvasFingerprint = canvas.toDataURL()

    // Collect device characteristics
    const deviceData = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset().toString(),
      navigator.hardwareConcurrency?.toString() || '0',
      navigator.maxTouchPoints?.toString() || '0',
      screen.colorDepth.toString(),
      window.devicePixelRatio.toString(),
      canvasFingerprint
    ].join('|')

    // Generate device ID hash
    const encoder = new TextEncoder()
    const data = encoder.encode(deviceData)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const deviceId = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

    return {
      deviceId,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      colorDepth: screen.colorDepth,
      pixelRatio: window.devicePixelRatio
    }
  }

  /**
   * Detect supported DRM systems
   */
  private async detectSupportedDRM(): Promise<string[]> {
    const supported: string[] = []

    // Test Widevine
    try {
      await navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
        initDataTypes: ['cenc'],
        audioCapabilities: [{ 
          contentType: 'audio/mp4; codecs="mp4a.40.2"',
          robustness: 'SW_SECURE_CRYPTO'
        }],
        videoCapabilities: [{ 
          contentType: 'video/mp4; codecs="avc1.42E01E"',
          robustness: 'SW_SECURE_CRYPTO'
        }]
      }])
      supported.push('widevine')
    } catch {
      // Widevine not supported
    }

    // Test FairPlay
    try {
      // @ts-ignore - FairPlay specific API
      if (window.WebKitMediaKeys) {
        supported.push('fairplay')
      }
    } catch {
      // FairPlay not supported
    }

    // Test PlayReady
    try {
      await navigator.requestMediaKeySystemAccess('com.microsoft.playready', [{
        initDataTypes: ['cenc'],
        audioCapabilities: [{ 
          contentType: 'audio/mp4; codecs="mp4a.40.2"',
          robustness: 'SW_SECURE_CRYPTO'
        }],
        videoCapabilities: [{ 
          contentType: 'video/mp4; codecs="avc1.42E01E"',
          robustness: 'SW_SECURE_CRYPTO'
        }]
      }])
      supported.push('playready')
    } catch {
      // PlayReady not supported
    }

    return supported
  }

  /**
   * Get optimal DRM system for current device
   */
  getOptimalDRMSystem(): string | null {
    if (this.supportedDRM.length === 0) {
      return null
    }

    const userAgent = navigator.userAgent

    // Prioritize based on platform
    if (userAgent.includes('Safari') && this.supportedDRM.includes('fairplay')) {
      return 'fairplay'
    }
    
    if (userAgent.includes('Edge') && this.supportedDRM.includes('playready')) {
      return 'playready'
    }
    
    if (this.supportedDRM.includes('widevine')) {
      return 'widevine'
    }

    return this.supportedDRM[0]
  }

  /**
   * Request DRM license from server
   */
  async requestLicense(cosmkId: string, userInfo: any, drmSystem?: string): Promise<LicenseResponse> {
    try {
      // Validate COSMKid
      if (!this.validateCOSMKid(cosmkId)) {
        throw new Error('Invalid COSMKid format')
      }

      if (!this.deviceFingerprint) {
        throw new Error('Device fingerprint not available')
      }

      const selectedDRM = drmSystem || this.getOptimalDRMSystem()
      if (!selectedDRM) {
        throw new Error('No supported DRM system found')
      }

      console.log(`Requesting ${selectedDRM} license for COSMK ID: ${cosmkId}`)

      const licenseRequest: LicenseRequest = {
        cosmkId,
        drmSystem: selectedDRM,
        deviceInfo: this.deviceFingerprint,
        userInfo
      }

      const response = await fetch('/api/drm/license', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Device-ID': this.deviceFingerprint.deviceId
        },
        body: JSON.stringify(licenseRequest)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `License request failed: ${response.status}`)
      }

      const licenseData = await response.json()
      
      if (licenseData.success && licenseData.licenseInfo) {
        // Store license locally
        this.activeLicenses.set(cosmkId, {
          ...licenseData.licenseInfo,
          acquiredAt: new Date().toISOString()
        })

        console.log(`License acquired successfully: ${licenseData.licenseInfo.licenseId}`)
      }

      return licenseData

    } catch (error) {
      console.error('License request failed:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Refresh an existing license
   */
  async refreshLicense(cosmkId: string, licenseId: string, userInfo: any): Promise<LicenseResponse> {
    try {
      if (!this.validateCOSMKid(cosmkId)) {
        throw new Error('Invalid COSMKid format')
      }

      console.log(`Refreshing license: ${licenseId}`)

      const response = await fetch('/api/drm/license/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Device-ID': this.deviceFingerprint?.deviceId || 'unknown'
        },
        body: JSON.stringify({
          cosmkId,
          licenseId,
          userInfo
        })
      })

      if (!response.ok) {
        throw new Error(`License refresh failed: ${response.status}`)
      }

      const licenseData = await response.json()
      
      if (licenseData.success && licenseData.licenseInfo) {
        // Update stored license
        this.activeLicenses.set(cosmkId, {
          ...licenseData.licenseInfo,
          refreshedAt: new Date().toISOString()
        })
      }

      return licenseData

    } catch (error) {
      console.error('License refresh failed:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Revoke a license
   */
  async revokeLicense(licenseId: string): Promise<boolean> {
    try {
      const response = await fetch('/api/drm/license/revoke', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Device-ID': this.deviceFingerprint?.deviceId || 'unknown'
        },
        body: JSON.stringify({ licenseId })
      })

      if (response.ok) {
        // Remove from local storage
        for (const [cosmkId, license] of this.activeLicenses.entries()) {
          if (license.licenseId === licenseId) {
            this.activeLicenses.delete(cosmkId)
            break
          }
        }
        return true
      }

      return false

    } catch (error) {
      console.error('License revocation failed:', error)
      return false
    }
  }

  /**
   * Get stored license for content
   */
  getLicense(cosmkId: string): any | null {
    return this.activeLicenses.get(cosmkId) || null
  }

  /**
   * Check if license is valid and not expired
   */
  isLicenseValid(cosmkId: string): boolean {
    const license = this.getLicense(cosmkId)
    if (!license) return false

    const now = new Date().getTime()
    const expiresAt = new Date(license.expiresAt).getTime()
    
    return now < expiresAt
  }

  /**
   * Get DRM capabilities
   */
  getDRMCapabilities(): DRMCapabilities {
    return {
      widevine: this.supportedDRM.includes('widevine'),
      fairplay: this.supportedDRM.includes('fairplay'),
      playready: this.supportedDRM.includes('playready'),
      userAgent: navigator.userAgent,
      platform: navigator.platform
    }
  }

  /**
   * Get device fingerprint
   */
  getDeviceFingerprint(): DeviceFingerprint | null {
    return this.deviceFingerprint
  }

  /**
   * Get all active licenses
   */
  getActiveLicenses(): Map<string, any> {
    return new Map(this.activeLicenses)
  }

  /**
   * Clear all stored licenses
   */
  clearLicenses(): void {
    this.activeLicenses.clear()
  }

  /**
   * Clean up expired licenses
   */
  cleanupExpiredLicenses(): void {
    const now = new Date().getTime()
    const expiredKeys: string[] = []

    for (const [cosmkId, license] of this.activeLicenses.entries()) {
      const expiresAt = new Date(license.expiresAt).getTime()
      if (now >= expiresAt) {
        expiredKeys.push(cosmkId)
      }
    }

    for (const key of expiredKeys) {
      this.activeLicenses.delete(key)
    }

    if (expiredKeys.length > 0) {
      console.log(`Cleaned up ${expiredKeys.length} expired licenses`)
    }
  }

  /**
   * Monitor concurrent streams
   */
  async checkConcurrentStreams(userInfo: any): Promise<boolean> {
    try {
      const response = await fetch('/api/drm/concurrent-check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Device-ID': this.deviceFingerprint?.deviceId || 'unknown'
        },
        body: JSON.stringify({
          userId: userInfo.userId,
          sessionId: userInfo.sessionId
        })
      })

      if (!response.ok) {
        return false
      }

      const result = await response.json()
      return result.allowed || false

    } catch (error) {
      console.error('Concurrent streams check failed:', error)
      return false
    }
  }
}

// Export singleton instance
export const drmClientService = new DRMClientService()
export default drmClientService